/* eslint-disable max-len */
/* eslint-disable camelcase */
import React, {ReactElement, ReactNode, useEffect, useState} from 'react';
import {Trans, useTranslation} from 'react-i18next';

import {
  Button,
  FormControl,
  FormHelperText,
  Typography,
  makeStyles,
  createStyles,
  Theme,
  List,
  ListItem,
  ListItemText,
  Link,
  Grid
} from '@material-ui/core';
import {useForm, Controller, SubmitHandler, FieldError} from 'react-hook-form';
import {useDispatch, useSelector} from 'react-redux';
// other imports

// shared imports
import {
  _setPatientPaymentsToken,
  _guarantorLogin
} from '@shared/store/actions/PaymentActions';

// scss import
import PaymentsStyledInput from '@components/system/FormInput/PaymentsStyledInput';
import './PaymentsLoginStyles.scss';
import {APP, GET_8_PIXEL_BOX, BLACK_COLOR} from 'src/style/variable';
// import ClinicGroupsIcon from '@components/system/MyIcons/ClinicGroupsIcon';
import ButtonLoader from '@components/system/ButtonLoader/ButtonLoader';
import NavRoutes from 'src/constants/route/NavRoutes';
import {
  verifyGuarantorLoginDetails,
  verifyGuarantorToken
} from '@shared/services/Payment.service';
// import {VerifyGuarantorTokenPaymentServiceMapper} from '@shared/services/service-mappers/PaymentServiceMapper';
import DateTimePickerWrapper from '@components/system/CustomDateTimePicker/DateTimePickerWrapper';
import moment from 'moment';
import InitSharedRepo from '../../../../../shared';
import MedopsLogo from '@components/system/MyIcons/MedopsLogo';
import PhoneFormatter from '@components/system/PhoneFormatter/PhoneFormatter';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import HelperUtils from '@shared/utils/HelperUtils';
import {VerifyGuarantorTokenPaymentServiceMapper} from '@shared/services/service-mappers/PaymentServiceMapper';
import {State} from '@shared/store/reducers/Index';
import {environment} from '@shared/environment/environment';
import {CustomRouteComponentProps} from 'src/constants/route/CustomRoute';
import Loader from '@components/framework/loader/Loader';
import PublicPageLayout from '../PublicPageLayout/PublicPageLayout';

const GUARANTOR_INFO_PORTAL_LOGIN_KEY = 'guarantorInfoFromPortalLogin';
const GUARANTOR_INFO_PAYMENT_TOKEN_KEY = 'guarantorInfoPaymentToken';
const GUARANTOR_INFO_RETURN_PATH_KEY = 'guarantorInfoReturnPath';
/** Single query param: when set, login returns to guarantor info instead of dashboard. */
const GUARANTOR_INFO_RETURN_TO_PARAM = 'returnTo';

function getReturnToPath(search: string): string | null {
  const returnToFromUrl = new URLSearchParams(search).get(
    GUARANTOR_INFO_RETURN_TO_PARAM
  );
  if (returnToFromUrl?.startsWith('/gi/')) {
    return returnToFromUrl;
  }

  const returnToFromStorage = sessionStorage.getItem(GUARANTOR_INFO_RETURN_PATH_KEY);
  if (returnToFromStorage?.startsWith('/gi/')) {
    return returnToFromStorage;
  }

  return null;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    loginButton: {
      padding: `${GET_8_PIXEL_BOX(1)} 0`,
      fontFamily: APP.FONT_FAMILY.BOLD,
      height: '3.5rem',
      width: '12rem',
      borderRadius: '1.75rem',
      boxShadow: 'rgba(0, 0, 0, 0.24) 0px 3px 8px'
    },
    fieldHeight: {
      '& .MuiInputBase-root': {
        height: '3.5rem'
      }
    },
    fieldText: {
      display: 'flex',
      color: BLACK_COLOR,
      paddingBottom: '0.5rem',
      justifyContent: 'flex-start',
      fontFamily: APP.FONT_FAMILY.MEDIUM,
      fontWeight: 500,
      fontSize: '1.25rem',
      lineHeight: '1.5rem'
    },
    patientNote: {
      display: 'flex',
      color: BLACK_COLOR,
      justifyContent: 'flex-start',
      textAlign: 'left',
      marginTop: '3rem',
      fontSize: '1.25rem',
      lineHeight: '1.35rem'
    },
    voiceAssistantButton: {
      marginTop: '1.5rem',
      padding: `${GET_8_PIXEL_BOX(1)} 0`,
      fontFamily: APP.FONT_FAMILY.BOLD,
      height: '3.5rem',
      width: '12rem',
      borderRadius: '1.75rem',
      boxShadow: 'rgba(0, 0, 0, 0.24) 0px 3px 8px'
    }
  })
);

interface LogInFormFields {
  guarantor_name: string;
  patient_name: string;
  patient_dob: moment.Moment | null;
}

const LoginFormDefaultValues: LogInFormFields = {
  patient_dob: null,
  patient_name: '',
  guarantor_name: ''
};

const PaymentsLogin = (
  props: CustomRouteComponentProps<{token: string}>
): ReactElement => {
  const classes = useStyles();
  const {t} = useTranslation();
  const [submitLoader, setSubmitLoader] = useState<boolean>(false);
  const [loginErrorMessage, setLoginErrorMessage] = useState<string>('');
  const {history, match, location} = props;
  /**
   * Fetching states from Redux
   */
  const isLogin = useSelector((state: State) => {
    return state.payments.isLogin;
  });
  const patientPaymentsToken = useSelector((state: State) => {
    return state.payments.patientPaymentsToken;
  });
  /**
   * Dispatcher to redux
   */
  const dispatch = useDispatch();
  const [
    apiResponseData,
    setApiResponseData
  ] = useState<VerifyGuarantorTokenPaymentServiceMapper>();
  const [loginAttempt, setLoginAttempt] = useState<number>(1);
  const [selectedClinicId] = useState<number>(0);

  const [isVerifyingToken, setIsVerifyingToken] = useState(true);

  const guarantorInfoReturnPath = React.useMemo(
    () => getReturnToPath(location.search),
    [location.search]
  );

  const navigateBackToGuarantorForm = (): void => {
    const returnTo = getReturnToPath(location.search);
    if (returnTo) {
      history.push(returnTo);
    }
  };

  const redirectToPaymentDashboard = (token: string) => {
    dispatch(_guarantorLogin(true));
    history.push(`/${NavRoutes.PUBLIC.PAYMENTS_DASHBOARD}/${token}`);
  };

  const redirectToGuarantorInfoAfterLogin = (returnTo: string) => {
    sessionStorage.setItem(GUARANTOR_INFO_PORTAL_LOGIN_KEY, '1');
    sessionStorage.setItem(GUARANTOR_INFO_PAYMENT_TOKEN_KEY, match.params.token);
    sessionStorage.removeItem(GUARANTOR_INFO_RETURN_PATH_KEY);
    dispatch(_guarantorLogin(true));
    const separator = returnTo.includes('?') ? '&' : '?';
    history.push(`${returnTo}${separator}prefill=1`);
  };

  const errorRedirect = React.useCallback(
    (errorMessage?: string): void => {
      history.push(`/${NavRoutes.PUBLIC.ERROR_PAY}`, {errorMessage});
    },
    [history]
  );

  const verifyGuarantorTokenApi = React.useCallback(() => {
    verifyGuarantorToken(selectedClinicId)
      .then(res => {
        if (res.data) {
          // setting up location to redux state.
          setApiResponseData(res.data);
          setIsVerifyingToken(false);
        }
      })
      .catch(err => {
        if (err) {
          errorRedirect(err?.message);
        }
      });
  }, [errorRedirect, selectedClinicId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // setting up token to redux State
    InitSharedRepo.initAfterAuth({token: match?.params?.token});
    dispatch(_setPatientPaymentsToken(match?.params?.token));
    const returnTo = getReturnToPath(location.search);
    if (returnTo) {
      sessionStorage.setItem(GUARANTOR_INFO_RETURN_PATH_KEY, returnTo);
    }
    if (isLogin) {
      if (returnTo) {
        redirectToGuarantorInfoAfterLogin(returnTo);
      } else {
        redirectToPaymentDashboard(patientPaymentsToken);
      }
      return;
    }
    dispatch(_guarantorLogin(false));
    verifyGuarantorTokenApi();
  }, [selectedClinicId]); // eslint-disable-line react-hooks/exhaustive-deps

  const {control, handleSubmit, errors} = useForm<LogInFormFields>({
    context: undefined,
    reValidateMode: undefined,
    shouldFocusError: false,
    shouldUnregister: false,
    defaultValues: LoginFormDefaultValues,
    criteriaMode: undefined,
    mode: 'all',

    resolver: async function (values) {
      // TODO: Add types for errorsObj
      const errorsObj: any = {};
      if (!values.guarantor_name) {
        errorsObj.guarantor_name = {
          message: 'PAYMENTS_LOGIN.VALIDATIONS.GUARANTOR_NAME'
        };
      }
      if (!values.patient_name) {
        errorsObj.patient_name = {
          message: 'PAYMENTS_LOGIN.VALIDATIONS.PATIENTS_NAME'
        };
      }
      if (!values.patient_dob) {
        errorsObj.patient_dob = {
          message: 'PAYMENTS_LOGIN.VALIDATIONS.PATIENTS_DOB'
        };
      } else if (
        !values.patient_dob.isValid() ||
        values.patient_dob.format('YYYY') < '1900' ||
        values.patient_dob.format('YYYY') > '2099'
      ) {
        errorsObj.patient_dob = {
          message: 'VALIDATORS.INVALID_DATE'
        };
      }
      return {values, errors: errorsObj};
    }
  });

  // Synchronous validation is being implemented here
  const handleFormSubmit: SubmitHandler<{
    guarantor_name: string;
    patient_name: string;
    patient_dob: moment.Moment;
  }> = async values => {
    setSubmitLoader(true);
    setLoginErrorMessage('');
    verifyGuarantorLoginDetails(
      {
        patient_first_name: values.patient_name,
        patient_dob: values.patient_dob.format('YYYY-MM-DD'),
        guarantor_name: values.guarantor_name,
        attempt: loginAttempt
      },
      selectedClinicId
    )
      .then(res => {
        if (res?.data?.token) {
          InitSharedRepo.initAfterAuth({token: res.data.token});
          dispatch(_setPatientPaymentsToken(match.params.token));
          const returnTo = getReturnToPath(location.search);
          if (returnTo) {
            redirectToGuarantorInfoAfterLogin(returnTo);
          } else {
          redirectToPaymentDashboard(match.params.token);
          }
        }
      })
      .catch(err => {
        setLoginErrorMessage(err.message || t('APP.ERROR'));
        setSubmitLoader(false);
        setLoginAttempt(loginAttempt + 1);
      });
  };

  // const renderLogo = React.useCallback(() => {
  //   let logoUrl = null;
  //   if (apiResponseData?.clinic_id) {
  //     // If clinic ID is present then show from clinic Details
  //     if (apiResponseData?.clinic_details?.clinic_logo_url) {
  //       logoUrl = apiResponseData?.clinic_details?.clinic_logo_url;
  //     }
  //   } else if (
  //     apiResponseData &&
  //     Array.isArray(apiResponseData?.locations) &&
  //     apiResponseData.locations.length > 0
  //   ) {
  //     const logo = apiResponseData.locations.filter(l => {
  //       return l.clinic_logo_url != null;
  //     });
  //     if (logo.length > 0) {
  //       logoUrl = logo[0].clinic_logo_url;
  //     }
  //   }
  //   if (logoUrl) {
  //     return (
  //       <img className="practice-icon" alt={'practice-logo'} src={logoUrl} />
  //     );
  //   }
  //   return <ClinicGroupsIcon className="practice-icon" />;
  // }, [apiResponseData]);

  const getSupportEmail = React.useCallback(() => {
    // Get contact email of the group (or) practice from api response to show as support email
    let email = null;
    // If patient shared is true, get contact email of group
    if (
      apiResponseData?.clinic_group_details?.patients_shared === true &&
      apiResponseData?.clinic_group_details?.contact_email
    ) {
      email = apiResponseData.clinic_group_details.contact_email;
    }
    // If patient shared is off, get contact email of practice
    else if (
      apiResponseData?.clinic_group_details?.patients_shared === false &&
      apiResponseData?.clinic_details?.contact_email
    ) {
      email = apiResponseData.clinic_details.contact_email;
    }
    // If contact email not found at group or clinic, show default support email
    else {
      email = environment.supportEmail;
    }
    return email;
  }, [apiResponseData]);

  const renderPortalHosTitle = React.useCallback(() => {
    if (apiResponseData?.clinic_id) {
      return apiResponseData.clinic_details.name;
    }
    return apiResponseData?.clinic_group_details.group_name;
  }, [apiResponseData]);

  const voiceAssistantShortCode = React.useMemo(() => {
    const clinicShortCode = apiResponseData?.clinic_details?.short_code;
    if (clinicShortCode) {
      return clinicShortCode;
    }

    const clinicId = apiResponseData?.clinic_id;
    if (clinicId && Array.isArray(apiResponseData?.locations)) {
      const matchedLocation = apiResponseData.locations.find(
        location => location.id === clinicId
      );
      return matchedLocation?.short_code || '';
    }

    return '';
  }, [apiResponseData]);

  const openVoiceAssistantInNewWindow = () => {
    if (!voiceAssistantShortCode) {
      return;
    }
    const path = `/${NavRoutes.PUBLIC.VOICE_ASSISTANT}/${voiceAssistantShortCode}`;
    window.open(`${window.location.origin}${path}`, '_blank', 'noopener,noreferrer');
  };

  const renderAddressLine1 = React.useCallback(() => {
    if (apiResponseData?.clinic_id) {
      return HelperUtils.formatFullAddress({
        line1: apiResponseData.clinic_details?.address,
        line2: apiResponseData.clinic_details?.address_line_2 || '',
        city: '',
        state: '',
        country: '',
        zip: ''
      });
    }
    return HelperUtils.formatFullAddress({
      line1: apiResponseData?.clinic_group_details?.address,
      line2: apiResponseData?.clinic_group_details?.address_line_2 || '',
      city: '',
      state: '',
      country: '',
      zip: ''
    });
  }, [apiResponseData]);

  const renderAddressLine2 = React.useCallback(() => {
    if (apiResponseData?.clinic_id) {
      return HelperUtils.formatFullAddress({
        line1: '',
        line2: '',
        city: apiResponseData?.clinic_details?.city,
        state: apiResponseData?.clinic_details?.state,
        country: '',
        zip: (apiResponseData?.clinic_details?.zip || '').toString()
      });
    }
    return HelperUtils.formatFullAddress({
      line1: '',
      line2: '',
      city: apiResponseData?.clinic_group_details?.city || '',
      state: apiResponseData?.clinic_group_details?.state || '',
      zip: (apiResponseData?.clinic_group_details?.zip || '').toString()
    });
  }, [apiResponseData]);

  const renderPracticePhones = (): ReactNode => {
    const clinicDetails = apiResponseData?.clinic_details;
    if (!clinicDetails) {
      return null;
    }

    const formatPhone = (phone: string): string =>
      phone.startsWith('+1') ? phone : `+1${phone}`;

    if (clinicDetails.phone === clinicDetails.billing_phone) {
      return clinicDetails.phone ? (
        <div className="clinic-phone">
          {t('PAYMENTS_LOGIN.PHONE_QUERY_LABEL')}
          <PhoneFormatter value={formatPhone(clinicDetails.phone)} />
        </div>
      ) : null;
    }

    return (
      <>
        {clinicDetails.phone ? (
          <div className="clinic-phone">
            {t('PAYMENTS_LOGIN.PHONE')}
            <PhoneFormatter value={formatPhone(clinicDetails.phone)} />
          </div>
        ) : null}
        {clinicDetails.billing_phone ? (
          <div className="clinic-phone">
            {t('PAYMENTS_LOGIN.BILLING_PHONE')}
            <PhoneFormatter value={formatPhone(clinicDetails.billing_phone)} />
          </div>
        ) : null}
      </>
    );
  };

  const renderAfterContent = (): ReactElement => (
    <div className="payment-login-after-content">
      <section className="flex-centering contact-section">
        <p className="contact-us-text">
          <Trans
            i18nKey="GUARANTOR_PORTAL.LOGIN_SCREEN.CONTACT_INFO"
            values={{
              supportEmail: getSupportEmail()
            }}
            components={[
              <Link href={`mailto:${getSupportEmail()}`} key="support-email" />
            ]}
          />
        </p>
      </section>

      <div className="tag-wrapper flex-centering">
        <p className="tag-text">
          <Trans
            i18nKey="GUARANTOR_PORTAL.LOGIN_SCREEN.APP_CLOSING_INFORMATION_PART1"
            components={[<span key="closing-information" />]}
          />
        </p>
        <p className="tag-text-2">
          {t('GUARANTOR_PORTAL.LOGIN_SCREEN.APP_CLOSING_INFORMATION_PART2')}
        </p>
        <Link href={`${environment.websiteDetails.name}`} target="pcl">
          <MedopsLogo className="logo" />
        </Link>
      </div>
    </div>
  );

  const renderInformation = (): JSX.Element => {
    const guarantorLoginNote =
      typeof apiResponseData?.clinic_details?.guarantor_login_note === 'string'
        ? apiResponseData.clinic_details.guarantor_login_note.trim()
        : '';
    const showNumberedList = guarantorLoginNote !== '';

    return (
      <Grid className="helper-information">
        {guarantorInfoReturnPath && (
          <div className="payment-login-go-back">
            <Link
              component="button"
              type="button"
              className="payment-login-go-back-link"
              onClick={navigateBackToGuarantorForm}>
              <ArrowBackIcon fontSize="small" />
              <span>{t('PAYMENTS_LOGIN.GO_BACK_TO_GUARANTOR_FORM')}</span>
            </Link>
          </div>
        )}
        <Typography component={'h1'} className="title">
          <Trans
            i18nKey="GUARANTOR_PORTAL.LOGIN_SCREEN.HELPER_TEXT_TITLE"
            components={[<span className="title-highlight" key="highlight" />]}
          />
        </Typography>
        <List component={'ol'} disablePadding>
          <ListItem disableGutters>
            <ListItemText
              primary={
                <Typography className="info-text">
                  {showNumberedList ? '1. ' : null}
                  {t('GUARANTOR_PORTAL.LOGIN_SCREEN.HELPER_TEXT_BULLET_1')}
                </Typography>
              }
            />
          </ListItem>
          {showNumberedList && (
              <ListItem disableGutters>
                <ListItemText
                  primary={
                    <Typography className="info-text">
                      2. {guarantorLoginNote}
                    </Typography>
                  }
                />
              </ListItem>
            )}
        </List>
      </Grid>
    );
  };

  const renderLoginForm = (): JSX.Element => {
    return (
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <div className={classes.fieldText}>
          {apiResponseData?.guarantor_details?.masked_name
            ? t('PAYMENTS_LOGIN.GUARANTOR_NAME', {
                guarantor_name: `${
                  apiResponseData?.guarantor_details?.masked_name ?? ''
                }`
              })
            : t('PAYMENTS_LOGIN.GUARANTOR_NAME_NO_ID')}
          *
        </div>
        <FormControl
          error={!!errors.guarantor_name}
          fullWidth={true}
          variant="outlined">
          <Controller
            id="guarantor_name"
            as={PaymentsStyledInput}
            control={control}
            name={'guarantor_name'}
            type={'text'}
            value={''}
            // label={t('PAYMENTS_LOGIN.GUARANTOR_NAME_NO_ID')}
            placeholder={
              apiResponseData?.guarantor_details?.masked_name
                ? t('PAYMENTS_LOGIN.PLACEHOLDERS.PATIENT_LAST_NAME')
                : t('PAYMENTS_LOGIN.PLACEHOLDERS.GUARANTOR_NAME')
            }
            defaultValue=""
            error={!!errors.guarantor_name}
            style={{height: '3.5rem'}}
          />
          {errors.guarantor_name && (
            <FormHelperText>
              {t(errors?.guarantor_name?.message || '')}
            </FormHelperText>
          )}
        </FormControl>
        <div className={classes.fieldText}>
          {t('PAYMENTS_LOGIN.PATIENTS_NAME')}*
        </div>
        <FormControl error={!!errors.patient_name} fullWidth variant="outlined">
          <Controller
            id="patient_name"
            as={PaymentsStyledInput}
            control={control}
            name={'patient_name'}
            type={'text'}
            value={''}
            // label={t('PAYMENTS_LOGIN.PATIENTS_NAME')}
            placeholder={t('PAYMENTS_LOGIN.PLACEHOLDERS.PATIENTS_NAME')}
            defaultValue=""
            error={!!errors.patient_name}
            style={{height: '3.5rem'}}
          />
          {errors.patient_name && (
            <FormHelperText>
              {t(errors?.patient_name?.message || '')}
            </FormHelperText>
          )}
        </FormControl>
        <div className={classes.fieldText}>
          {t('PAYMENTS_LOGIN.PATIENTS_DOB')}*
        </div>
        <FormControl error={!!errors.patient_dob} fullWidth variant="outlined">
          <Controller
            id="patient_date_of_birth"
            render={({onChange, onBlur, value}): React.ReactElement => (
              <DateTimePickerWrapper
                className={classes.fieldHeight}
                value={value}
                onChange={onChange}
                onBlur={onBlur}
                placeholder={t('PAYMENTS_LOGIN.PLACEHOLDERS.PATIENTS_DOB')}
                aria-placeholder={'Patients date of birth'}
                error={!!errors?.patient_dob}
              />
            )}
            control={control}
            name={'patient_dob'}
            // error={!!errors.patient_dob}
          />
          {errors.patient_dob && (
            <FormHelperText>
              {t(((errors.patient_dob as unknown) as FieldError).message || '')}
            </FormHelperText>
          )}
        </FormControl>

        {loginErrorMessage != '' && (
          <div className="error-message">{loginErrorMessage}</div>
        )}
        <div className="flex-centering">
          <Button
            variant="contained"
            size={'medium'}
            color="primary"
            className={[classes.loginButton].join(' ')}
            type={'submit'}
            onClick={handleSubmit(handleFormSubmit)}>
            {submitLoader ? <ButtonLoader /> : 'SUBMIT'}
          </Button>
        </div>

        {voiceAssistantShortCode && (
          <div className="flex-centering">
            <Button
              variant="outlined"
              size="medium"
              color="primary"
              className={classes.voiceAssistantButton}
              endIcon={<OpenInNewIcon />}
              onClick={openVoiceAssistantInNewWindow}>
              {t('PAYMENTS_LOGIN.VOICE_ASSISTANT_BUTTON')}
            </Button>
          </div>
        )}

        <div className={[classes.patientNote, 'flex-centering'].join(' ')}>
          {apiResponseData?.clinic_details?.clinic_contact_text ||
            t('ADD_CLINIC.DEFAULT_VALUE.CLINIC_CONTACT_TEXT')}
        </div>
      </form>
    );
  };

  if (isVerifyingToken === true) {
    return (
      <div className="flex-centering payment-login-loader">
        <Loader size={56} color="primary" />
      </div>
    );
  }

  return (
    <PublicPageLayout
      className="payment-login-page"
      testId="pcl-guarantor-loginPage"
      showHero={false}
      contentLayout="card"
      practiceBadgeLabel={renderPortalHosTitle()}
      // practiceBadgeIcon={renderLogo()}
      practiceName={renderPortalHosTitle()}
      addressLine1={renderAddressLine1()}
      addressLine2={renderAddressLine2()}
      practicePhones={renderPracticePhones()}
      afterContent={renderAfterContent()}>
      <div className="panel helper-information-panel">{renderInformation()}</div>
      <div className="panel login-panel">{renderLoginForm()}</div>
    </PublicPageLayout>
  );
};

export default PaymentsLogin;
